/**
 * DIAGNOSTIC SANITIZER — the explicit redaction boundary required before
 * ANY diagnostic text could ever be escalated to an AI provider (Phase D
 * §14, §18). Nothing in this app currently sends diagnostics anywhere —
 * there is no provider integration — but the Support Assistant runs
 * user-typed problem descriptions through this boundary regardless, so
 * the redaction behavior is real and testable today, not a promise for
 * later.
 */
const REDACTION_PATTERNS: { pattern: RegExp; label: string }[] = [
  { pattern: /\b(sk-[a-zA-Z0-9]{16,}|sk-ant-[a-zA-Z0-9-]{16,})\b/g, label: 'API key' },
  { pattern: /\b[A-Za-z0-9_-]*(api[_-]?key|secret|token)[A-Za-z0-9_-]*\s*[:=]\s*['"]?[A-Za-z0-9._\-]{8,}['"]?/gi, label: 'API key/token' },
  { pattern: /\bBearer\s+[A-Za-z0-9._-]{10,}\b/gi, label: 'Bearer token' },
  { pattern: /\bpassword\s*[:=]\s*['"]?\S+['"]?/gi, label: 'password' },
  { pattern: /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, label: 'private key' },
  { pattern: /\b[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, label: 'JWT/session token' },
  { pattern: /\bcookie\s*[:=]\s*['"]?[^;\n]{6,}['"]?/gi, label: 'cookie' },
];

export interface SanitizeResult {
  sanitized: string;
  redactedCount: number;
  redactedLabels: string[];
}

/** Redacts anything resembling a credential from free-typed diagnostic
 * text before it's shown as "context to be shared" — see §18/§19. */
export function sanitizeDiagnosticText(input: string): SanitizeResult {
  let sanitized = input;
  const redactedLabels: string[] = [];
  let redactedCount = 0;
  for (const { pattern, label } of REDACTION_PATTERNS) {
    const matches = sanitized.match(pattern);
    if (matches && matches.length > 0) {
      redactedCount += matches.length;
      redactedLabels.push(label);
      sanitized = sanitized.replace(pattern, `[REDACTED — ${label}]`);
    }
  }
  return { sanitized, redactedCount, redactedLabels: Array.from(new Set(redactedLabels)) };
}
